import { Grid } from "@mui/material"
import React, { useCallback, useContext, useEffect, useState } from "react"
import { useLocation, useSearchParams } from "react-router-dom"
import { customPalette } from "../../../theme"
import { CustomPagination } from "../../components/buttons/pagination/CustomPagination"
import { IconInbox } from "../../components/custom-icons/icon-inbox"
import { pagesUrl } from "../../core/appConstants"
import { AppNavBarContext } from "../../core/context/nav-bar/AppNavBarContext"
import { PROJECT_ITEMS_PER_PAGE, ProjectListContext } from "../../core/context/project/ProjectListContext"
import { AppSideBarContext } from "../../core/context/AppSideBarContext/AppSideBarContext"
import { ProjectStatusEnum, projectStatusEnumLabels } from "../../core/enum/projectStatusEnum"
import { enumToSelectOptions } from "../../core/services/helper-service"
import { SearchAndFilter } from "../../components/FilterListDrawer/SearchAndFilter"
import { UserContext } from "../../core/context/user/user-context"
import { isMegaUser } from "../../core/services/authentication-service"
import { ProjectForm } from "./components/ProjectForm"
import { ProjectList } from "./components/ProjectList"
import { CustomBottomNavigation } from "../../components/buttons/navigate-button/CustomBottomNavigation"

export function ProjectsHomePage(): React.JSX.Element {
  return <ProjectsHomePageCore />
}

function ProjectsHomePageCore(): React.JSX.Element {
  const [searchParams] = useSearchParams()
  const location = useLocation()

  const { user } = useContext(UserContext)
  const { setShowAppSide, setFullScreen } = useContext(AppSideBarContext)
  const { setTitle, setPreviousUrl, setIcon, setBreadcrumbSteps } = useContext(AppNavBarContext)
  const {
    statusFilter,
    searchState,
    setPage,
    setSearchState,
    setStatusFilter,
    page,
    triggerSearch,
    projectCount,
    fetchOnlyMyProject,
    setFetchOnlyMyProject,
  } = useContext(ProjectListContext)

  const [isOpenCreateProjectForm, setIsOpenCreateProjectForm] = useState<boolean>(false)

  const openCreateProjectForm: () => void = useCallback(() => {
    setIsOpenCreateProjectForm(true)
  }, [])

  const handleClose: () => void = useCallback(() => {
    setIsOpenCreateProjectForm(false)
  }, [])

  useEffect(() => {
    const shouldCreate = searchParams.get("create")
    if (shouldCreate === "true") {
      openCreateProjectForm()
    }
  }, [searchParams, openCreateProjectForm])

  useEffect(() => {
    setShowAppSide(true)
    setTitle("Projets")
    setPreviousUrl(pagesUrl.HOME)
    setBreadcrumbSteps([])
    setIcon(<IconInbox sx={{ color: customPalette.black, fontWeight: "2px" }} />)
  }, [setShowAppSide, location, setTitle, setPreviousUrl, setIcon, setFullScreen, setBreadcrumbSteps])

  const handleSelectChange: (value: ProjectStatusEnum) => void = useCallback(
    (value) => {
      setStatusFilter(value)
    },
    [setStatusFilter]
  )

  const handleSearch: (newSearchState: string) => void = useCallback(
    (newSearchState) => {
      setPage(1)
      setSearchState(newSearchState)
    },
    [setPage, setSearchState]
  )

  const handleSearchPagination: (event: React.ChangeEvent<unknown>, newPage: number) => void = useCallback(
    (event, newPage) => {
      event.preventDefault()
      // This trigger a useEffect in ProjectListContext to re-run the search
      setPage(newPage)
    },
    [setPage]
  )

  return (
    <Grid id="project-home-page" container spacing={1} sx={{ pt: 2, height: "78vh" }}>
      <Grid container item xs={12} columnGap={3} sx={{ mb: 1 }}>
        <SearchAndFilter
          searchStateLabel="Rechercher par nom, numéro d’affaire"
          searchState={searchState}
          handleSearch={handleSearch}
          selectedOption={statusFilter}
          labelFilter="Statut"
          options={enumToSelectOptions(ProjectStatusEnum, projectStatusEnumLabels).filter(
            (selectedOption) => selectedOption.value !== ProjectStatusEnum.DELETED
          )}
          handleChange={handleSelectChange}
          triggerSearch={triggerSearch}
          isFilterDrawer={isMegaUser(user)}
          setFetchOnlyMyData={setFetchOnlyMyProject}
          fetchOnlyMyData={fetchOnlyMyProject}
        />
        <ProjectList />
      </Grid>

      <CustomPagination
        count={Math.ceil(projectCount / PROJECT_ITEMS_PER_PAGE)}
        page={page}
        handlePagination={handleSearchPagination}
      />
      <CustomBottomNavigation actionLabel="Créer un projet" validateAction={openCreateProjectForm} />
      <ProjectForm open={isOpenCreateProjectForm} handleClose={handleClose} />
    </Grid>
  )
}
